import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { deepMerge } from '@/api';
import { getRouteLanguage, redirectFromBaseURL } from './languageDetection';
// Import docs translation files
import arDocs from './locales/ar-docs.json';
// Import integrations translation files
import arIntegrations from './locales/ar-integrations.json';
// Strings shared with the absorbed component library (glob_navigation,
// glob_footer, auth screens, ...). Merged first so app files win on collision.
import arShared from './locales/ar-shared.json';
// Import formshive-specific translation files
import arUnique from './locales/ar-unique.json';
import deDocs from './locales/de-docs.json';
import deIntegrations from './locales/de-integrations.json';
import deShared from './locales/de-shared.json';
import deUnique from './locales/de-unique.json';
import enDocs from './locales/en-docs.json';
import enIntegrations from './locales/en-integrations.json';
// App-specific integration page strings (formshive.integrations.*).
// Other locales fall back to English until a translator gets to them.
import enIntegrationsApp from './locales/en-integrations-app.json';
import enShared from './locales/en-shared.json';
// Shared integrations namespace. English only; other locales fall back to it.
import enSharedIntegrations from './locales/en-shared-integrations.json';
import enUnique from './locales/en-unique.json';
import esDocs from './locales/es-docs.json';
import esIntegrations from './locales/es-integrations.json';
import esShared from './locales/es-shared.json';
import esUnique from './locales/es-unique.json';
import frDocs from './locales/fr-docs.json';
import frIntegrations from './locales/fr-integrations.json';
import frShared from './locales/fr-shared.json';
import frUnique from './locales/fr-unique.json';
import ptDocs from './locales/pt-docs.json';
import ptIntegrations from './locales/pt-integrations.json';
import ptShared from './locales/pt-shared.json';
import ptUnique from './locales/pt-unique.json';
import thDocs from './locales/th-docs.json';
import thIntegrations from './locales/th-integrations.json';
import thShared from './locales/th-shared.json';
import thUnique from './locales/th-unique.json';
import zhDocs from './locales/zh-docs.json';
import zhIntegrations from './locales/zh-integrations.json';
import zhShared from './locales/zh-shared.json';
import zhUnique from './locales/zh-unique.json';

// One i18next instance for the whole app, including the absorbed shared
// components. The `*-shared` files come first in each chain so an app string
// overrides a shared one of the same key.
//
// IMPORTANT: use deepMerge, not spread. Both `<lang>-integrations.json` and
// `en-shared-integrations.json` write a top-level `integrations` object, and a
// shallow spread would replace one subtree wholesale rather than merging, so
// the UI would render raw `integrations.form.*` keys.
const resources = {
  en: {
    translation: deepMerge(
      enShared,
      enSharedIntegrations,
      enUnique,
      enDocs,
      enIntegrations,
      enIntegrationsApp
    ),
  },
  de: {
    translation: deepMerge(
      deShared,
      enSharedIntegrations,
      deUnique,
      deDocs,
      deIntegrations,
      enIntegrationsApp
    ),
  },
  es: {
    translation: deepMerge(
      esShared,
      enSharedIntegrations,
      esUnique,
      esDocs,
      esIntegrations,
      enIntegrationsApp
    ),
  },
  fr: {
    translation: deepMerge(
      frShared,
      enSharedIntegrations,
      frUnique,
      frDocs,
      frIntegrations,
      enIntegrationsApp
    ),
  },
  pt: {
    translation: deepMerge(
      ptShared,
      enSharedIntegrations,
      ptUnique,
      ptDocs,
      ptIntegrations,
      enIntegrationsApp
    ),
  },
  zh: {
    translation: deepMerge(
      zhShared,
      enSharedIntegrations,
      zhUnique,
      zhDocs,
      zhIntegrations,
      enIntegrationsApp
    ),
  },
  th: {
    translation: deepMerge(
      thShared,
      enSharedIntegrations,
      thUnique,
      thDocs,
      thIntegrations,
      enIntegrationsApp
    ),
  },
  ar: {
    translation: deepMerge(
      arShared,
      enSharedIntegrations,
      arUnique,
      arDocs,
      arIntegrations,
      enIntegrationsApp
    ),
  },
};

// Turn a stored/browser preference into a URL prefix before anything renders,
// so the router and i18n below both read the same, final hash.
if (typeof window !== 'undefined') {
  redirectFromBaseURL();
}

// Initialize i18n instance for formshive
// Apps don't use LanguageDetector - they use synchronous init + detectAndSetLanguage in App.tsx.
// `lng` comes from the route, not from storage: LanguageRoutes derives the
// language from the same hash prefix, and seeding a different one here makes
// the two fight on the first render.
export const i18nInitPromise = i18n.use(initReactI18next).init({
  resources,
  lng: typeof window !== 'undefined' ? getRouteLanguage() : 'en',
  fallbackLng: 'en',
  defaultNS: 'translation',
  interpolation: {
    escapeValue: false,
  },
});

export default i18n;
